import type { UserInfo } from "@foundation/reg-ui-client";

import { isClient } from "~utils/isClient";

const TWITTER_HANDLE = "markets";
const MOBILE_BREAKPOINT = "(max-width:767px)";

// Util functions below extracted from lightsaber/main/src/~components/Share/handlers.ts

export const toEncodedQueryParams = (params: {
  [key: string]: string;
}): string => {
  return Object.keys(params).reduce((accumulator, currentKey) => {
    const key = currentKey;
    const value = encodeURIComponent(params[key]);
    const param = `${key}=${value}`;

    if (!accumulator) {
      return param;
    }

    return `${accumulator}&${param}`;
  }, "");
};

export const decorateCanonicalWithUTMParams = (
  canonical: string,
  campaign: string
) => {
  const queryParams = toEncodedQueryParams({
    utm_source: "website",
    utm_medium: "share",
    utm_campaign: campaign,
  });

  return `${canonical}?${queryParams}`;
};

export const handleSocialClick = (
  href: string,
  action: string,
  label: string,
  event: Event,
  userInfo: UserInfo & { user?: { referralToken?: string } }
) => {
  if (!isClient()) return;

  event.preventDefault();

  window.dataLayer?.push({
    event: "quote_social_share",
    eventCategory: "social",
    eventAction: action,
    eventLabel: label,
  });

  const linkUrl = userInfo?.user?.referralToken
    ? `${href}${encodeURIComponent(`?sref=${userInfo.user.referralToken}`)}`
    : href;

  const mobileMediaQuery = window.matchMedia(MOBILE_BREAKPOINT);

  if (mobileMediaQuery.matches) {
    window.open(linkUrl, "_self");

    return;
  }

  const customWindowParams =
    "menubar=no,toolbar=no,resizable=yes,scrollbars=yes,width=626,height=438";

  window.open(linkUrl, "", customWindowParams);
};

export const handleCopyClick = (url: string, label: string) => {
  if (!isClient()) {
    return;
  }

  window.dataLayer?.push({
    event: "quote_social_share",
    eventCategory: "social",
    eventAction: "share",
    eventLabel: label,
  });

  // navigator clipboard api needs a secure context (eg. https)
  if (navigator.clipboard && window.isSecureContext) {
    navigator.clipboard.writeText(url);
  }
};

export const facebookShareHandler = ({
  canonical,
  event,
  userInfo,
}: {
  canonical: string;
  event: Event;
  userInfo: UserInfo;
}) => {
  const canonicalWithUTMParams = decorateCanonicalWithUTMParams(
    canonical,
    "facebook"
  );

  const queryParams = toEncodedQueryParams({
    u: canonicalWithUTMParams,
  });

  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?${queryParams}`;

  return handleSocialClick(
    facebookShareUrl,
    "share",
    "facebook",
    event,
    userInfo
  );
};

export const twitterShareHandler = ({
  canonical,
  shareText,
  event,
  userInfo,
}: {
  canonical: string;
  shareText: string;
  event: Event;
  userInfo: UserInfo;
}) => {
  const canonicalWithUTMParams = decorateCanonicalWithUTMParams(
    canonical,
    "twitter"
  );

  // Note - twitter auto adds '@' prefix to handles here, so be sure to remove.
  const queryParams = toEncodedQueryParams({
    text: shareText || "",
    via: TWITTER_HANDLE,
    url: canonicalWithUTMParams,
  });

  const twitterShareUrl = `https://twitter.com/share?${queryParams}`;

  return handleSocialClick(
    twitterShareUrl,
    "share",
    "twitter",
    event,
    userInfo
  );
};

export const linkedInShareHandler = ({
  canonical,
  shareText,
  summary = "",
  event,
  userInfo,
}: {
  canonical: string;
  shareText: string;
  summary?: string;
  event: Event;
  userInfo: UserInfo;
}) => {
  const canonicalWithUTMParams = decorateCanonicalWithUTMParams(
    canonical,
    "linkedin"
  );

  const queryParams = toEncodedQueryParams({
    mini: "true",
    title: shareText,
    summary,
    url: canonicalWithUTMParams,
  });

  const linkedInShareUrl = `https://www.linkedin.com/shareArticle?${queryParams}`;

  return handleSocialClick(
    linkedInShareUrl,
    "share",
    "linkedin",
    event,
    userInfo
  );
};

export const emailShareHandler = ({
  canonical,
  shareText = "",
  event,
  userInfo,
}: {
  canonical: string;
  shareText?: string;
  event: Event;
  userInfo: UserInfo;
}) => {
  const canonicalWithUTMParams = decorateCanonicalWithUTMParams(
    canonical,
    "email"
  );

  const queryParams = toEncodedQueryParams({
    subject: shareText,
    body: `${shareText}\r\n\r\n${canonicalWithUTMParams}`,
  });

  const href = `mailto:?${queryParams}`;

  return handleSocialClick(href, "share", "email", event, userInfo);
};

export const copyLinkHandler = ({ canonical }: { canonical: string }) => {
  const canonicalWithUTMParams = decorateCanonicalWithUTMParams(
    canonical,
    "copy"
  );

  return handleCopyClick(canonicalWithUTMParams, "link");
};
